import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import axios, { setCurrentCurrencyCode } from '@/lib/axios'
import { getErrorMessage } from '@/lib/utils'
import { MetaData } from '@/types/meta'

type MetaStateType = {
  isLoading: boolean
  response: MetaData | null
  error: {
    status?: number
    message: string
  } | null
}

type MetaStoreType = {
  metaState: MetaStateType
  getMeta: () => Promise<MetaData>
  setMeta: (data: MetaData) => void
  reset: () => void
}

const initialState: MetaStateType = {
  isLoading: false,
  response: null,
  error: null
}

const useMetaStore = create<MetaStoreType>()(
  devtools(set => ({
    metaState: initialState,
    getMeta: async () => {
      set(() => ({
        metaState: {
          ...initialState,
          isLoading: true
        }
      }))

      try {
        const { data } = await axios.get('/shared/essential/meta')

        // Set default currency for all API requests
        if (data.generalSettings?.defaultCurrencyCode) {
          setCurrentCurrencyCode(data.generalSettings.defaultCurrencyCode)
        }

        set(() => ({
          metaState: {
            ...initialState,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          metaState: {
            ...initialState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))

        throw error
      }
    },
    setMeta: (data: MetaData) => {
      if (data.generalSettings?.defaultCurrencyCode) {
        setCurrentCurrencyCode(data.generalSettings.defaultCurrencyCode)
      }
      set(() => ({
        metaState: {
          ...initialState,
          response: data
        }
      }))
    },
    reset: () => {
      set(() => ({
        metaState: initialState
      }))
    }
  }))
)

export default useMetaStore
